import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { Container, Draggable } from "react-smooth-dnd";
import { arrayMoveImmutable } from "array-move";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import {
  getProductReviewMediaApi,
  addProductReviewMediaApi,
  updateProductReviewMediaApi,
  deleteProductReviewMediaApi,
  reorderProductReviewMediaApi,
} from "../../../../services/manage-product/productService";

// Kept in sync with config('product.review_media_image_max_kb')/'review_media_video_max_kb'
// in the backend (defaults 5MB image / 100MB video). The server remains the source of
// truth — these constants only give editors immediate feedback before upload.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/x-ms-wmv", "video/webm"];

const sortByPosition = (rows) =>
  [...rows].sort((a, b) => (a.display_order - b.display_order) || (a.id - b.id));

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted. Same convention as
// manageWearItToModal.jsx / manageHowToStyleVideoModal.jsx.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

const extractValidationMessage = (err, fallback) => {
  if (err && typeof err === "object" && !err.isAxiosError) {
    const fieldErrors = err.errors;
    if (fieldErrors && typeof fieldErrors === "object") {
      const firstField = Object.values(fieldErrors)[0];
      if (Array.isArray(firstField) && firstField.length) return firstField[0];
    }
    if (err.message) return err.message;
  }
  return fallback;
};

const validateFile = (file) => {
  const isVideo = file.type.startsWith("video/");
  if (isVideo) {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) return "Only MP4, MOV, AVI, WMV, or WebM videos are allowed.";
    if (file.size > MAX_VIDEO_BYTES) return `Video exceeds the ${Math.round(MAX_VIDEO_BYTES / (1024 * 1024))}MB size limit.`;
  } else {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "Only JPG, PNG, GIF, or WEBP images are allowed.";
    if (file.size > MAX_IMAGE_BYTES) return `Image exceeds the ${Math.round(MAX_IMAGE_BYTES / (1024 * 1024))}MB size limit.`;
  }
  return null;
};

/**
 * Single upload/replace/delete row for one review media entry. No text fields at all —
 * every entry is media-only, so there's no separate add/edit form modal like Wear It To
 * has; a file input directly drives create (row === null) and replace (row present).
 */
const ReviewMediaRow = ({ row, productId, nextDisplayOrder, busy, onUploaded, onDeleted }) => {
  const [rowBusy, setRowBusy] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const validationMessage = validateFile(file);
    if (validationMessage) {
      setFileError(validationMessage);
      resetFileInput();
      return;
    }
    setFileError("");
    setRowBusy(true);

    const request = row
      ? updateProductReviewMediaApi(row.id, productId, file, row.display_order)
      : addProductReviewMediaApi(productId, file, nextDisplayOrder);

    request
      .then(({ response }) => {
        toast.success(row ? "Review media replaced." : "Review media added.");
        onUploaded(response);
      })
      .catch((err) => {
        setFileError(extractValidationMessage(err, "Failed to upload the file."));
        notifyMutationError(err, "Failed to upload the file.");
      })
      .finally(() => {
        setRowBusy(false);
        resetFileInput();
      });
  };

  const handleDelete = () => {
    SweetAlert.fire({
      title: "Delete this review media entry?",
      html: "This will permanently remove the image/video.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setRowBusy(true);
      deleteProductReviewMediaApi(row.id)
        .then(() => {
          toast.success("Review media deleted.");
          onDeleted(row.id);
        })
        .catch((err) => {
          if (err?.isAxiosError && err.response?.status === 404) {
            onDeleted(row.id);
            return;
          }
          notifyMutationError(err, "Failed to delete the entry.");
        })
        .finally(() => setRowBusy(false));
    });
  };

  const disabled = busy || rowBusy;

  if (!row) {
    // The persistent "add new" slot at the end of the list.
    return (
      <div className="border rounded p-3 mb-3 text-center">
        <input
          ref={fileInputRef}
          type="file"
          className="form-control-file"
          accept="image/*,video/*"
          disabled={disabled}
          onChange={handleFileSelected}
        />
        {fileError && <div className="text-danger small mt-1">{fileError}</div>}
        {rowBusy && <PreLoader withoutText />}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center border-bottom py-2" style={{ background: "#fff", position: "relative" }}>
      {rowBusy && <PreLoader withoutText />}
      <span
        className="drag-handle"
        style={{
          cursor: disabled ? "not-allowed" : "grab",
          pointerEvents: disabled ? "none" : "auto",
          opacity: disabled ? 0.4 : 1,
          marginRight: 8,
          flexShrink: 0,
        }}
      >
        <DragHandleIcon />
      </span>

      {Number(row.media_type) === 2 ? (
        <video
          muted
          src={row.media}
          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, marginRight: 12, flexShrink: 0, background: "#000" }}
        />
      ) : (
        <img
          src={row.media}
          alt=""
          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, marginRight: 12, flexShrink: 0 }}
        />
      )}

      <div style={{ flex: 1, minWidth: 0 }}>
        <small className="text-muted text-capitalize">{Number(row.media_type) === 2 ? "video" : "image"}</small>
        {fileError && <div className="text-danger small">{fileError}</div>}
      </div>

      <div className="d-flex align-items-center" style={{ gap: 12, marginLeft: 12, flexShrink: 0 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          disabled={disabled}
          style={{ display: "none" }}
          onChange={handleFileSelected}
        />
        <Button
          size="sm"
          color="primary"
          outline
          disabled={disabled}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          Replace
        </Button>
        <Button size="sm" color="link" className="p-0 text-danger" disabled={disabled} onClick={handleDelete}>
          <i className="icon-trash" />
        </Button>
      </div>
    </div>
  );
};

const ManageReviewMediaModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reordering, setReordering] = useState(false);

  const fetchEntries = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    getProductReviewMediaApi(productId)
      .then(({ response }) => {
        setEntries(sortByPosition(Array.isArray(response) ? response : []));
      })
      .catch((err) => {
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load review media.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    setEntries([]);
    setLoadError("");
    setLoading(false);
    setReordering(false);

    if (open && productId) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const handleUploaded = () => {
    fetchEntries(true);
  };

  const handleDeleted = (id) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const handleReorderDrop = ({ removedIndex, addedIndex }) => {
    if (reordering || removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;

    const lastKnownGoodOrder = entries;
    const reordered = arrayMoveImmutable(entries, removedIndex, addedIndex).map((row, index) => ({
      ...row,
      display_order: index,
    }));

    setEntries(reordered);
    setReordering(true);

    const items = reordered.map((row) => ({ id: row.id, display_order: row.display_order }));

    reorderProductReviewMediaApi(productId, items)
      .then(({ response }) => {
        setEntries(sortByPosition(Array.isArray(response) ? response : reordered));
      })
      .catch((err) => {
        setEntries(lastKnownGoodOrder);
        notifyMutationError(err, "Failed to save the new order.");
      })
      .finally(() => setReordering(false));
  };

  return (
    <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
      <ModalHeader toggle={onClose} tag="div">
        <div>Real Reviews, Real People</div>
        {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
          <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
            {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </ModalHeader>
      <ModalBody style={{ minHeight: 200, position: "relative", maxHeight: "70vh", overflowY: "auto" }}>
        {loading && <PreLoader withoutText />}

        {!loading && loadError && (
          <div className="alert alert-danger">
            <div className="font-weight-bold mb-1">Review media could not be loaded</div>
            <div className="mb-2">{loadError}</div>
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchEntries()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && (
          <>
            {entries.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={handleReorderDrop}>
                  {entries.map((row) => (
                    <Draggable key={row.id}>
                      <ReviewMediaRow
                        row={row}
                        productId={productId}
                        busy={reordering}
                        onUploaded={handleUploaded}
                        onDeleted={handleDeleted}
                      />
                    </Draggable>
                  ))}
                </Container>
              </div>
            ) : (
              <div className="text-muted mb-3">No review media entries yet.</div>
            )}

            <div className="font-weight-bold mb-2">Add Review Media</div>
            <ReviewMediaRow
              row={null}
              productId={productId}
              nextDisplayOrder={entries.length ? Math.max(...entries.map((e) => e.display_order)) + 1 : 0}
              busy={reordering}
              onUploaded={handleUploaded}
              onDeleted={handleDeleted}
            />
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ManageReviewMediaModal;
